/**
 * Performance Settings Component
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the usePerformanceSettings hook.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Save, BookOpen } from 'lucide-react';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { useAuth } from '../../contexts/AuthContext';
import { useViewKey, useViewGroup } from '../../contexts/ViewContext';
import { coreAPI } from '../../lib/api';
import type { Company } from '../../lib/api';
import type { ScopeSelection } from '../../lib/api/performanceSettings';

// Import modular components
import {
  OTIFSettingsTab,
  PPMSettingsTab,
  RatingFormulaTab,
  GradeThresholdsTab,
  GradeColourPaletteSection,
  PerformanceGuideTab,
  SiteKPISettingsTab,
  KpiMilestonesTab,
  DeliveryMetricsRecalcCard,
  usePerformanceSettings,
  useOTIFGradeThresholds,
  usePPMGradeThresholds,
  SettingsScopeSelector,
  buildScopeOptions,
} from './performance';

export default function PerformanceSettings() {
  const [activeTab, setActiveTab] = useState('otif');
  // CR-7274: which scope the tab is configuring. Undefined until the options
  // load, which keeps the first fetch on the caller's own context — the
  // pre-existing behaviour, so the tab is never blank while sites load.
  const [scopeTarget, setScopeTarget] = useState<ScopeSelection | undefined>(undefined);
  const [scopeValue, setScopeValue] = useState('');
  const [sites, setSites] = useState<Company[]>([]);
  const qualityLabel = useQualityMeasureLabel();
  const { user } = useAuth();
  const viewKey = useViewKey();
  const viewGroup = useViewGroup();
  // Mirrors backend is_customer_group_admin() and AdminSetup's own gate. A
  // group-scope request is refused 403 for anyone else, so without this check
  // pointing a site-level customer admin at a group view would turn a working
  // tab into an error page.
  const isGroupAdmin = Boolean(
    user?.company_type === 'helix' || user?.is_group_admin,
  );
  // Whether the scope on screen was CHOSEN by the user or merely reflects the
  // site they are on. Only the former is worth preserving, and only until the
  // view changes — see the two effects below. A ref, not state: it must not
  // itself trigger a render, and it is read inside an effect that runs after
  // the render which set it.
  const userPickedScope = useRef(false);
  const isAdmin = Boolean(
    user?.is_superuser || user?.company_type === 'helix' || user?.is_company_admin,
  );

  useEffect(() => {
    let cancelled = false;
    coreAPI
      .getAccessibleCompanies()
      .then((response) => {
        if (!cancelled) setSites(response.data.results ?? []);
      })
      .catch(() => {
        // A failed site list must not blank the settings tab: without options
        // the picker simply does not render and the tab behaves as it did
        // before CR-7274.
        if (!cancelled) setSites([]);
      });
    return () => {
      cancelled = true;
    };
    // Refetched on a view-context change, not once on mount: a platform admin
    // switching tenants gets a different accessible-site list, and the picker
    // would otherwise go on offering the previous tenant's sites.
  }, [viewKey]);

  const scopeOptions = useMemo(
    () =>
      buildScopeOptions(
        sites
          .filter((site) => site.company_type === 'customer')
          .map((site) => ({
            id: site.id,
            name: site.name,
            group: site.group,
            group_name: site.group_name,
          })),
      ),
    [sites],
  );

  const handleScopeChange = useCallback(
    (option: { value: string; selection: ScopeSelection }) => {
      userPickedScope.current = true;
      setScopeValue(option.value);
      setScopeTarget(option.selection);
    },
    [],
  );

  const {
    // State
    loading,
    saving,
    settings,
    hasChanges,
    formData,
    // Threshold state
    // CR-7274: Clear override — drops this scope's own row so it inherits again
    clearOverride,
    clearingOverride,
    editingThreshold,
    thresholdEditData,
    savingThreshold,
    showAddThreshold,
    newThresholdData,
    addingThreshold,
    // State setters
    setShowAddThreshold,
    // Handlers
    handleInputChange,
    handleWeightChange,
    handleSave,
    handleResetThresholds,
    handleResetOTIFMappings,
    handleResetPPMMappings,
    handleEditThreshold,
    handleCancelEditThreshold,
    handleSaveThreshold,
    handleThresholdFieldChange,
    handleAddThreshold,
    handleDeleteThreshold,
    handleNewThresholdFieldChange,
    loadSettings,
  } = usePerformanceSettings(scopeTarget);

  // A scope picked under the PREVIOUS view does not survive a site switch.
  // It names a company that the new context may not contain at all, so
  // keeping it would leave the picker asserting one company while the page
  // below showed another — and, worse, aim the next Save at the company the
  // user had just navigated away from.
  //
  // On mount this sets the values they already hold, which React discards
  // without a re-render, so it costs nothing to let it run then too.
  useEffect(() => {
    userPickedScope.current = false;
    setScopeValue('');
    setScopeTarget(undefined);
  }, [viewKey]);

  // CR-7274: name the scope already on screen instead of showing "Select a
  // scope" over a page that is plainly showing one. Leaving it blank was
  // finding #1 of this ticket ("nothing names the company being edited")
  // reintroduced by the very control meant to fix it.
  //
  // The guard is "has the user picked a scope", NOT "is the picker empty".
  // That distinction is what makes a site switch land: the effect above
  // clears the value, but the settings for the new site are still in flight,
  // so this runs once against the PREVIOUS response and would latch the old
  // company in permanently if an empty picker were its only trigger. Keyed on
  // the user's intent instead, it simply corrects itself when the new
  // settings arrive.
  useEffect(() => {
    if (userPickedScope.current || scopeOptions.length === 0) return;

    // The switcher is pointed at a GROUP, so the group's default is what the
    // tab should be configuring — pointing the app at a group and configuring
    // that group are one gesture, not two.
    //
    // This branch sets the TARGET as well as the label, which the site branch
    // below deliberately does not. The server cannot infer it: with no scope
    // `performance_config_company` always resolves a COMPANY, and there is no
    // input by which it would answer with a group. So the group scope only
    // exists if the request asks for it by name.
    if (viewGroup && isGroupAdmin) {
      const groupValue = `group:${viewGroup.id}`;
      if (scopeOptions.some((option) => option.value === groupValue)) {
        setScopeValue(groupValue);
        setScopeTarget({ scope: 'group', id: viewGroup.id });
        return;
      }
    }

    if (!settings?.company) return;
    const current = `site:${settings.company}`;
    // Never select a value the picker cannot offer — in an "All Companies"
    // view there is no site to name, and the placeholder is the honest answer.
    if (scopeOptions.some((option) => option.value === current)) {
      setScopeValue(current);
    }
  }, [settings?.company, scopeOptions, viewGroup, isGroupAdmin]);


  // CR-6267: OTIF colour bands are their own resource — separate state and
  // endpoint from the supplier grade thresholds above.
  const otifGrades = useOTIFGradeThresholds(settings?.otif_grade_thresholds ?? [], loadSettings);

  // CR-6652: PPM colour bands are their own resource — separate state and
  // endpoint from OTIF grades and supplier rating thresholds.
  const ppmGrades = usePPMGradeThresholds(
    settings?.ppm_grade_thresholds ?? [],
    loadSettings,
    qualityLabel,
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading performance settings...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Supplier Performance Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure OTIF and {qualityLabel} calculation thresholds, rating formula weights, and score mappings.
          </p>
        </div>
        <Button onClick={handleSave} disabled={!hasChanges || saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* CR-7274: "Configure settings for" — the picker, the Inherited /
          Overridden badge, and Clear override. Hidden when there is nothing
          to choose between, so a single-site customer sees no extra chrome. */}
      {scopeOptions.length > 1 && (
        <SettingsScopeSelector
          options={scopeOptions}
          value={scopeValue}
          onChange={handleScopeChange}
          scope={settings?.settings_scope}
          owner={settings?.settings_owner}
          loading={loading}
          onClearOverride={clearOverride}
          clearing={clearingOverride}
          disabled={!isAdmin}
        />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="otif">OTIF Settings</TabsTrigger>
          <TabsTrigger value="ppm">{qualityLabel} Settings</TabsTrigger>
          <TabsTrigger value="rating">Rating Formula</TabsTrigger>
          <TabsTrigger value="grades">Grade Thresholds</TabsTrigger>
          <TabsTrigger value="kpi">Site KPI Settings</TabsTrigger>
          <TabsTrigger value="milestones">KPI Milestones</TabsTrigger>
          <TabsTrigger value="guide" className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            Guide
          </TabsTrigger>
        </TabsList>

        {/* OTIF Settings Tab */}
        <TabsContent value="otif" className="space-y-4">
          {isAdmin && <DeliveryMetricsRecalcCard />}
          <OTIFSettingsTab
            settings={settings}
            formData={formData}
            otifGrades={otifGrades}
            onInputChange={handleInputChange}
            onResetOTIFMappings={handleResetOTIFMappings}
          />
        </TabsContent>

        {/* PPM Settings Tab */}
        <TabsContent value="ppm">
          <PPMSettingsTab
            settings={settings}
            formData={formData}
            ppmGrades={ppmGrades}
            onInputChange={handleInputChange}
            onResetPPMMappings={handleResetPPMMappings}
          />
        </TabsContent>

        {/* Rating Formula Tab */}
        <TabsContent value="rating">
          <RatingFormulaTab
            formData={formData}
            onWeightChange={handleWeightChange}
          />
        </TabsContent>

        {/* Grade Thresholds Tab */}
        <TabsContent value="grades" className="space-y-4">
          {/* CR-7079: one palette, cascaded to every scorecard. Sits above the
              band editor because a colour change here overrides the per-band
              colour below for any standard grade. */}
          <GradeColourPaletteSection
            settings={settings}
            onSaved={loadSettings}
            readOnly={!isAdmin}
          />
          <GradeThresholdsTab
            settings={settings}
            editingThreshold={editingThreshold}
            thresholdEditData={thresholdEditData}
            savingThreshold={savingThreshold}
            showAddThreshold={showAddThreshold}
            newThresholdData={newThresholdData}
            addingThreshold={addingThreshold}
            onSetShowAddThreshold={setShowAddThreshold}
            onResetThresholds={handleResetThresholds}
            onEditThreshold={handleEditThreshold}
            onCancelEditThreshold={handleCancelEditThreshold}
            onSaveThreshold={handleSaveThreshold}
            onThresholdFieldChange={handleThresholdFieldChange}
            onAddThreshold={handleAddThreshold}
            onDeleteThreshold={handleDeleteThreshold}
            onNewThresholdFieldChange={handleNewThresholdFieldChange}
          />
        </TabsContent>

        {/* Site KPI Settings Tab (CR-5883) */}
        <TabsContent value="kpi">
          <SiteKPISettingsTab />
        </TabsContent>

        {/* KPI Milestones Tab (CR-5880): own endpoint, saves per row — not the Save Changes button. */}
        <TabsContent value="milestones">
          <KpiMilestonesTab canEdit={isGroupAdmin} />
        </TabsContent>

        {/* Guide Tab */}
        <TabsContent value="guide">
          <PerformanceGuideTab
            settings={settings}
            formData={formData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
